import { ExpenseTypeEnum } from '@app/enums/ExpenseTypes';
import { RequestTypesKeys } from '@app/enums/RequestTypes';
import { Column } from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { PaymentRequestStatus, TravelRequestStatus } from '@models/request';
import { StudyExpense } from '@models/study';
import { StudyExpenseTrackingDataRow } from '@models/studyExpenseTrackingDataRow';
import { maxDateValue, maxNumberValue, minDateValue, minNumberValue } from '@utility/constants';

export function isExpenseTypeEditable(rowData: StudyExpenseTrackingDataRow): boolean {
    return (
        (rowData.requestType === RequestTypesKeys.REIMBURSEMENT || rowData.statusId === 0) && !isInVoidedStatus(rowData)
    );
}

export function isInVoidedStatus(rowData: StudyExpenseTrackingDataRow): boolean {
    return rowData.requestType !== RequestTypesKeys.TRAVEL && rowData.statusId === PaymentRequestStatus.Voided;
}

export function isPaymentReadOnlyStatus(rowData: StudyExpenseTrackingDataRow | undefined): boolean {
    return (
        !!rowData &&
        rowData.requestType !== RequestTypesKeys.TRAVEL &&
        (rowData.statusId === PaymentRequestStatus.PendingPSApproval ||
            rowData.statusId === PaymentRequestStatus.PQProcessing)
    );
}

export function isNotInFinalStatus(rowData: StudyExpenseTrackingDataRow): boolean {
    return (
        (rowData.requestType !== RequestTypesKeys.TRAVEL && rowData.statusId !== PaymentRequestStatus.Voided) ||
        (rowData.requestType === RequestTypesKeys.TRAVEL && rowData.statusId !== TravelRequestStatus.ERSubmitted)
    );
}

export function isPayment(rowData: StudyExpenseTrackingDataRow): boolean {
    return rowData.requestType !== RequestTypesKeys.TRAVEL;
}

export function isTravelWithReceipt(rowData: StudyExpenseTrackingDataRow): boolean {
    return rowData.requestType === RequestTypesKeys.TRAVEL && !!rowData.travelReceiptId;
}

export function isPaymentBeforePendingPSApproval(rowData: StudyExpenseTrackingDataRow): boolean {
    return (
        rowData.requestType !== RequestTypesKeys.TRAVEL &&
        rowData.statusId !== PaymentRequestStatus.Voided &&
        rowData.statusId !== PaymentRequestStatus.ERSubmitted &&
        rowData.statusId !== PaymentRequestStatus.PendingERSubmission &&
        rowData.statusId !== PaymentRequestStatus.Denied
    );
}

/**
 * Mirrors `isPossibleToEditRequest` in request.component.ts: statuses in which
 * a Payment request (Reimbursement/Stipend) can never be edited, regardless of
 * access level.
 */
function isPaymentEditableStatus(rowData: StudyExpenseTrackingDataRow): boolean {
    return (
        rowData.statusId !== PaymentRequestStatus.PendingPSApproval &&
        rowData.statusId !== PaymentRequestStatus.Denied &&
        rowData.statusId !== PaymentRequestStatus.PendingERSubmission &&
        rowData.statusId !== PaymentRequestStatus.ERSubmitted &&
        rowData.statusId !== PaymentRequestStatus.Voided
    );
}

/**
 * Mirrors the Payment Request card's `showEditSaveButton$` status gating
 * (see payment-request-card.component.ts), combined with the
 * `isPossibleToEditRequest` gate above, to decide whether the Notes field of
 * a Reimbursement/Stipend row can be edited.
 */
export function isPaymentNotesEditable(rowData: StudyExpenseTrackingDataRow, elevatedAccess: boolean): boolean {
    if (!isPaymentEditableStatus(rowData)) return false;

    if (elevatedAccess) {
        // PendingPSApproval is intentionally excluded here: although it is part of the
        // elevated-access status list in showEditSaveButton$, isPaymentEditableStatus above
        // (mirroring isPossibleToEditRequest) always disallows editing in that status.
        return [
            PaymentRequestStatus.PendingApproval,
            PaymentRequestStatus.PaymentInProcess,
            PaymentRequestStatus.SetUpFailed,
            PaymentRequestStatus.PendingSetUp,
            PaymentRequestStatus.Submitted,
            PaymentRequestStatus.PendingSiteVerification,
            PaymentRequestStatus.Draft,
            PaymentRequestStatus.PayoutIssue,
        ].includes(rowData.statusId);
    }

    return [
        PaymentRequestStatus.Submitted,
        PaymentRequestStatus.PendingSiteVerification,
        PaymentRequestStatus.Draft,
    ].includes(rowData.statusId);
}

/**
 * Mirrors the travel request's `travelCardEditable$` status gating
 * (see request.component.ts) to decide whether the Notes field of a Travel
 * row can be edited.
 */
export function isTravelNotesEditable(rowData: StudyExpenseTrackingDataRow, elevatedAccess: boolean): boolean {
    if (elevatedAccess) return true;

    return [TravelRequestStatus.Draft, TravelRequestStatus.Submitted].includes(rowData.statusId);
}

export function isRequestNotesEditable(rowData: StudyExpenseTrackingDataRow, elevatedAccess: boolean): boolean {
    return rowData.requestType === RequestTypesKeys.TRAVEL
        ? isTravelWithReceipt(rowData) && isTravelNotesEditable(rowData, elevatedAccess)
        : isPaymentNotesEditable(rowData, elevatedAccess);
}

export function isPossibleToAddRow(rowData: StudyExpenseTrackingDataRow) {
    return (
        rowData.requestType === RequestTypesKeys.REIMBURSEMENT &&
        rowData.expenseId !== 0 &&
        rowData.statusId !== PaymentRequestStatus.Voided &&
        rowData.statusId !== PaymentRequestStatus.ERSubmitted &&
        rowData.statusId !== PaymentRequestStatus.PendingERSubmission &&
        !isPaymentReadOnlyStatus(rowData)
    );
}

export function isNotMileage(rowData: StudyExpenseTrackingDataRow): boolean {
    return (
        rowData.requestType === RequestTypesKeys.REIMBURSEMENT &&
        (rowData.expenseType as StudyExpense)?.expenseId !== ExpenseTypeEnum.Mileage
    );
}

export function getRequestIdentifier(requestId: number, requestType: RequestTypesKeys): string {
    switch (requestType) {
        case RequestTypesKeys.TRAVEL:
            return `T-${requestId}`;
        case RequestTypesKeys.STIPEND:
            return `S-${requestId}`;
        case RequestTypesKeys.REIMBURSEMENT:
            return `R-${requestId}`;
        default:
            return '';
    }
}

export function parseNumberField(oldVal: number, newVal: string, column: Column): number {
    const minNumber: number = column.getColDef().cellEditorParams['min'] ?? minNumberValue;
    const maxNumber: number = column.getColDef().cellEditorParams['max'] ?? maxNumberValue;

    const parsedNumber = Number(newVal);

    if (!isNaN(parsedNumber) && parsedNumber >= minNumber && parsedNumber <= maxNumber) {
        return parsedNumber;
    }

    return oldVal;
}

export function parseTextField(newVal: string, column: Column): string {
    const maxLength: number | undefined = column.getColDef().cellEditorParams['maxLength'];

    return maxLength ? newVal.slice(0, maxLength) : newVal;
}

export function parseDateField(oldVal: string, newVal: string, column: Column): string {
    const minDate: Date = column.getColDef().cellEditorParams['min'] ?? minDateValue;
    const maxDate: Date = column.getColDef().cellEditorParams['max'] ?? maxDateValue;

    const parsedDate = Date.parse(newVal);

    if (!isNaN(parsedDate) && parsedDate >= minDate.getTime() && parsedDate <= maxDate.getTime()) {
        return newVal;
    }

    return oldVal;
}
